import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import type { Company, Project } from '@/entities/directory'
import { CompanyDot, directoryErrorMessage, directoryQueryOptions } from '@/entities/directory'
import type { TagEntityKind } from '@/entities/tag'
import { entityTagsQueryOptions, TagChips } from '@/entities/tag'
import { CreateCompanyDialog } from '@/features/create-company'
import { CreateProjectDialog } from '@/features/create-project'
import { EditCompanyDialog } from '@/features/edit-company'
import { EditProjectDialog } from '@/features/edit-project'
import {
  useToggleCompanyVisibility,
  useToggleProjectVisibility,
} from '@/features/toggle-visibility'
import { cn, pluralRu } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { Menu } from '@/shared/ui/menu'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { toast } from '@/shared/ui/toast'

interface CompaniesSearch {
  hidden?: boolean
}

export const Route = createFileRoute('/_app/companies/')({
  // Тумблер «показывать скрытые» живёт в URL (шарится, переживает reload).
  // Дефолт (false) в схему не пишем — URL чистый.
  validateSearch: (s: Record<string, unknown>): CompaniesSearch =>
    s.hidden === true || s.hidden === 'true' ? { hidden: true } : {},
  component: CompaniesPage,
})

function projectIds(companies: Company[]): Set<string> {
  return new Set(companies.flatMap((c) => c.projects.map((p) => p.id)))
}

function CompaniesPage() {
  const { hidden: includeHidden = false } = Route.useSearch()
  const navigate = Route.useNavigate()

  // CompanyView/ProjectView не несут hidden — берём два серверных набора (полный
  // и видимый), а per-row hidden выводим их разницей (00-architecture § 4).
  const visibleQ = useQuery(directoryQueryOptions(false))
  const fullQ = useQuery(directoryQueryOptions(true))

  const [creatingCompany, setCreatingCompany] = useState(false)
  const [editingCompany, setEditingCompany] = useState<Company | null>(null)
  const [creatingProjectFor, setCreatingProjectFor] = useState<Company | null>(null)
  const [editingProject, setEditingProject] = useState<Project | null>(null)

  const isPending = visibleQ.isPending || fullQ.isPending
  const companies = (includeHidden ? fullQ.data : visibleQ.data) ?? []

  const visibleCompanyIds = new Set((visibleQ.data ?? []).map((c) => c.id))
  const visibleProjectIds = projectIds(visibleQ.data ?? [])
  const isCompanyHidden = (id: string) => !visibleCompanyIds.has(id)
  const isProjectHidden = (id: string) => !visibleProjectIds.has(id)

  const active = companies.filter((c) => !c.isArchived)
  const archived = companies.filter((c) => c.isArchived)

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        title="Компании"
        description="Компании-клиенты и их проекты — номенклатура линз календаря."
        actions={
          <Button variant="primary" size="sm" onClick={() => setCreatingCompany(true)}>
            <Icon name="plus" size={14} />
            Компания
          </Button>
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-gutter pb-safe-8">
        <div className="flex items-center justify-end py-2">
          <Checkbox
            label="Показывать скрытые"
            checked={includeHidden}
            onChange={(e) =>
              void navigate({ search: e.target.checked ? { hidden: true } : {}, replace: true })
            }
          />
        </div>

        {isPending && (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        )}

        {!isPending && active.length === 0 && (
          <EmptyState
            title="Компаний пока нет"
            description="Заведите первую компанию-клиента — события можно будет привязывать к ней и смотреть её линзу."
            action={
              <Button variant="secondary" size="sm" onClick={() => setCreatingCompany(true)}>
                Завести компанию
              </Button>
            }
          />
        )}

        <ul className="flex flex-col gap-2">
          {active.map((company) => (
            <CompanyRow
              key={company.id}
              company={company}
              hidden={isCompanyHidden(company.id)}
              isProjectHidden={isProjectHidden}
              onEdit={() => setEditingCompany(company)}
              onAddProject={() => setCreatingProjectFor(company)}
              onEditProject={setEditingProject}
            />
          ))}
        </ul>

        {archived.length > 0 && (
          <details className="mt-6">
            <summary className="label cursor-pointer select-none">
              Архив · {archived.length}{' '}
              {pluralRu(archived.length, 'компания', 'компании', 'компаний')}
            </summary>
            <ul className="mt-2 flex flex-col gap-2 opacity-60">
              {archived.map((company) => (
                <CompanyRow
                  key={company.id}
                  company={company}
                  hidden={isCompanyHidden(company.id)}
                  isProjectHidden={isProjectHidden}
                  onEdit={() => setEditingCompany(company)}
                  onAddProject={() => setCreatingProjectFor(company)}
                  onEditProject={setEditingProject}
                />
              ))}
            </ul>
          </details>
        )}
      </div>

      <CreateCompanyDialog open={creatingCompany} onClose={() => setCreatingCompany(false)} />
      <EditCompanyDialog company={editingCompany} onClose={() => setEditingCompany(null)} />
      <CreateProjectDialog
        company={creatingProjectFor}
        onClose={() => setCreatingProjectFor(null)}
      />
      <EditProjectDialog project={editingProject} onClose={() => setEditingProject(null)} />
    </div>
  )
}

const ROW_TAGS_MAX = 3

// Теги строки справочника (00-architecture § 5): набор принадлежит хабу и общий
// для сервисов экосистемы, поэтому читается отдельной ручкой /api/tags/{kind}/{id}.
// Здесь только read-view — плотная строка дерева не тянет второй редактор с
// диалогом; правка тегов живёт на странице линзы компании/проекта.
function EntityTagChips({ entityKind, entityId }: { entityKind: TagEntityKind; entityId: string }) {
  const tags = useQuery(entityTagsQueryOptions(entityKind, entityId))
  return <TagChips tags={tags.data} max={ROW_TAGS_MAX} className="flex items-center gap-1.5" />
}

function CompanyRow({
  company,
  hidden,
  isProjectHidden,
  onEdit,
  onAddProject,
  onEditProject,
}: {
  company: Company
  hidden: boolean
  isProjectHidden: (id: string) => boolean
  onEdit: () => void
  onAddProject: () => void
  onEditProject: (p: Project) => void
}) {
  const toggle = useToggleCompanyVisibility(company.id)
  const projects = company.projects.filter((p) => !p.isArchived)
  const archivedProjects = company.projects.filter((p) => p.isArchived)

  const toggleHidden = () =>
    toggle.mutate(!hidden, {
      onError: (e) => toast.error(directoryErrorMessage(e, 'Не удалось изменить видимость')),
    })

  return (
    // Компания — «шапка» карточки: светлее фона (bg-surface), крупнее кегль.
    // Её проекты живут уровнем ниже — на bg-elevated за hairline (см. блок ниже).
    <li
      className={cn('overflow-hidden rounded-card border bg-bg-elevated', hidden && 'opacity-60')}
    >
      {/* На телефоне строка компании переносится: имя занимает первую строку
          целиком, счётчик/теги — вторую, действия остаются справа. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 bg-bg-surface px-3 py-2.5 sm:flex-nowrap sm:px-4 sm:py-3">
        <CompanyDot color={company.color} className="size-2.5" />
        <Link
          to="/companies/$companyId"
          params={{ companyId: company.id }}
          className="min-w-0 flex-1 truncate text-[15px] font-medium transition-colors hover:text-accent-hover sm:flex-none"
        >
          {company.name}
        </Link>
        {hidden && <span className="label shrink-0 text-text-tertiary">скрыто</span>}
        <span className="shrink-0 rounded-pill border px-2 py-0.5 text-xs text-text-tertiary">
          {projects.length > 0
            ? `${projects.length} ${pluralRu(projects.length, 'проект', 'проекта', 'проектов')}`
            : 'без проектов'}
        </span>
        <EntityTagChips entityKind="company" entityId={company.id} />
        <div className="ml-auto flex items-center gap-1">
          {/* Подпись сворачивается в sr-only: на узком экране остаётся «+». */}
          <Button variant="ghost" size="sm" onClick={onAddProject}>
            <Icon name="plus" size={14} />
            <span className="sr-only sm:not-sr-only">Проект</span>
          </Button>
          <Menu
            aria-label={`Действия: ${company.name}`}
            trigger={<Icon name="dots" size={16} />}
            items={[
              { label: 'Изменить', icon: 'pencil', onSelect: onEdit },
              {
                label: hidden ? 'Показать в calendar' : 'Скрыть в calendar',
                icon: hidden ? 'eye' : 'eye-off',
                disabled: toggle.isPending,
                onSelect: toggleHidden,
              },
            ]}
          />
        </div>
      </div>

      {(projects.length > 0 || archivedProjects.length > 0) && (
        <div className="border-t px-3 py-2">
          {/* Вертикальная гайд-линия — вложенность проектов в компанию читается
              без чтения текста, как ветка дерева. */}
          <ul className="flex flex-col border-l border-border-strong pl-3">
            {[...projects, ...archivedProjects].map((project) => (
              <ProjectRow
                key={project.id}
                project={project}
                companyColor={company.color}
                hidden={isProjectHidden(project.id)}
                onEdit={() => onEditProject(project)}
              />
            ))}
          </ul>
        </div>
      )}
    </li>
  )
}

function ProjectRow({
  project,
  companyColor,
  hidden,
  onEdit,
}: {
  project: Project
  companyColor: string
  hidden: boolean
  onEdit: () => void
}) {
  const toggle = useToggleProjectVisibility(project.id)

  const toggleHidden = () =>
    toggle.mutate(!hidden, {
      onError: (e) => toast.error(directoryErrorMessage(e, 'Не удалось изменить видимость')),
    })

  return (
    <li
      className={cn(
        'flex items-center gap-2 rounded-control px-2 py-2 transition-colors hover:bg-bg-surface sm:py-1.5',
        (project.isArchived || hidden) && 'opacity-50',
      )}
    >
      <CompanyDot color={project.color || companyColor} className="size-1.5" />
      <Link
        to="/projects/$projectId"
        params={{ projectId: project.id }}
        className="min-w-0 truncate text-sm text-text-secondary transition-colors hover:text-text-primary"
      >
        {project.name}
      </Link>
      {hidden && <span className="text-xs text-text-tertiary">скрыто</span>}
      {project.isArchived && <span className="text-xs text-text-tertiary">архив</span>}
      <EntityTagChips entityKind="project" entityId={project.id} />
      <div className="ml-auto flex items-center">
        <Menu
          aria-label={`Действия: проект ${project.name}`}
          trigger={<Icon name="dots" size={14} />}
          items={[
            { label: 'Изменить', icon: 'pencil', onSelect: onEdit },
            {
              label: hidden ? 'Показать в calendar' : 'Скрыть в calendar',
              icon: hidden ? 'eye' : 'eye-off',
              disabled: toggle.isPending,
              onSelect: toggleHidden,
            },
          ]}
        />
      </div>
    </li>
  )
}
